import { BlockTypes } from 'enum/block-type.enum'
import { useEffect, useState, useCallback, useRef } from 'react'
import GetLoadPercentage from '../api/administration/blocks/GetLoadPercentage'
import { sleep } from './others.helper'

interface ScormLoadPercentageProps {
  block: {
    id?: number
    type?: BlockTypes
    processing?: boolean
    loadPercentage?: number
  }
  delay?: number
  onFinish?: () => void
  onLoad?: (percentage: number) => void
}

export const useScormLoadPercentage = ({
  block,
  delay = 5000,
  onFinish,
  onLoad,
}: ScormLoadPercentageProps) => {
  const { type } = block
  const isScorm = type === BlockTypes.BlockScorm
  const showPercentageBase = isScorm && block.processing
  const onFinishRef = useRef(onFinish)
  const onLoadRef = useRef(onLoad)
  useEffect(() => {
    onFinishRef.current = onFinish
  }, [onFinish])
  useEffect(() => {
    onLoadRef.current = onLoad
  }, [onLoad])

  const [showPercentage, setShowPercentage] = useState(showPercentageBase)
  const [percentage, setPercentage] = useState(block.loadPercentage)

  const reload = useCallback(() => {
    if (block.id)
      return GetLoadPercentage(block.id)
        .then((data) => {
          setPercentage(data.loadPercentage)
          return data
        })
        .catch(() => undefined)
  }, [block.id])

  useEffect(() => {
    let mount = true
    const init = async () => {
      let lastPercentage = block.loadPercentage || 0
      let diff: number | undefined
      let interval = true
      while (mount && interval) {
        const res = await reload()
        if (res) {
          const { processed, processing, loadPercentage } = res
          interval = !processed || processing
          const currentDiff = loadPercentage - lastPercentage || 1
          diff = diff ? currentDiff + diff / 2 : currentDiff
        }
        const sleepTime = diff ? delay / diff : delay
        mount && setShowPercentage(interval)
        lastPercentage = res?.loadPercentage || 0
        onLoadRef.current?.(lastPercentage)
        await sleep(sleepTime)
      }
      mount && onFinishRef.current?.()
    }
    if (showPercentageBase) init()
    return () => {
      mount = false
    }
  }, [reload, showPercentageBase, block.id, block.loadPercentage, delay])

  return {
    showPercentage: showPercentageBase && showPercentage,
    percentage,
    reload,
  }
}
